// Forms Module
// Handles form validation and submission

export class FormManager {
    constructor() {
        this.forms = new Map();
    }

    // Initialize form validation
    initializeValidation() {
        const forms = document.querySelectorAll('.needs-validation');
        Array.from(forms).forEach(form => {
            form.addEventListener('submit', event => {
                if (!form.checkValidity()) {
                    event.preventDefault();
                    event.stopPropagation();
                }
                form.classList.add('was-validated');
            });
        });
    }

    // Initialize AJAX forms
    initializeAjaxForms() {
        const ajaxForms = document.querySelectorAll('[data-ajax-form]');
        ajaxForms.forEach(form => {
            form.addEventListener('submit', (e) => {
                e.preventDefault();
                this.handleAjaxForm(form);
            });
        });
    }

    // Handle AJAX form submission
    handleAjaxForm(form) {
        const formData = new FormData(form);
        const submitBtn = form.querySelector('[type="submit"]');
        const originalText = submitBtn.textContent;
        
        // Show loading state
        submitBtn.disabled = true;
        submitBtn.innerHTML = '<span class="spinner-border spinner-border-sm me-2"></span>Processing...';
        
        fetch(form.action || window.location.href, {
            method: 'POST',
            body: formData,
            headers: {
                'X-Requested-With': 'XMLHttpRequest'
            }
        })
        .then(response => response.json())
        .then(data => {
            if (data.success) {
                this.showNotification(data.message || 'Success!', 'success');
                form.reset();
                form.classList.remove('was-validated');
            } else {
                this.showNotification(data.message || 'An error occurred.', 'danger');
            }
        })
        .catch(error => {
            console.error('Error:', error);
            this.showNotification('An error occurred. Please try again.', 'danger');
        })
        .finally(() => {
            // Reset button state
            submitBtn.disabled = false;
            submitBtn.textContent = originalText;
        });
    }

    // Initialize booking form
    initializeBookingForm() {
        // Try to find the form immediately
        let form = document.getElementById('booking-form');
        
        if (!form) {
            // If form not found, set up a mutation observer to watch for it
            this.setupBookingFormObserver();
            return;
        }

        this.setupBookingFormEventListeners(form);
    }
    
    // Set up event listeners for booking form
    setupBookingFormEventListeners(form) {
        // Remove any existing listeners to prevent duplicates
        form.removeEventListener('submit', this.handleBookingFormSubmit);
        
        form.addEventListener('submit', (e) => {
            e.preventDefault();
            this.handleBookingFormSubmit(form);
        });

        // Also add direct click handler to the button as backup
        const bookButton = document.getElementById('book-appointment-btn');
        if (bookButton) {
            bookButton.addEventListener('click', (e) => {
                e.preventDefault();
                this.handleBookingFormSubmit(form);
            });
        }

        // Handle cancel booking
        const cancelBtn = document.getElementById('cancel-booking');
        if (cancelBtn) {
            cancelBtn.addEventListener('click', () => {
                document.getElementById('booking-form-container').style.display = 'none';
                form.reset();
            });
        }
    }
    
    // Set up observer to watch for booking form when it becomes visible
    setupBookingFormObserver() {
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
                    const target = mutation.target;
                    if (target.id === 'booking-form-container' && target.style.display !== 'none') {
                        const form = document.getElementById('booking-form');
                        if (form && !form.hasAttribute('data-form-initialized')) {
                            console.log('FormManager: Booking form became visible, initializing');
                            form.setAttribute('data-form-initialized', 'true');
                            this.setupBookingFormEventListeners(form);
                        }
                    }
                }
            });
        });
        
        const bookingContainer = document.getElementById('booking-form-container');
        if (bookingContainer) {
            observer.observe(bookingContainer, { attributes: true });
        }
    }

    // Handle booking form submission
    handleBookingFormSubmit(form) {
        const formData = new FormData(form);
        const submitBtn = form.querySelector('[type="submit"]');
        const originalText = submitBtn.textContent;
        
        // Show loading state
        submitBtn.disabled = true;
        submitBtn.innerHTML = '<span class="spinner-border spinner-border-sm me-2"></span>Booking...';
        
        // Use appointment manager for booking
        if (window.appraisalTheme && window.appraisalTheme.appointmentManager) {
            window.appraisalTheme.appointmentManager.bookAppointment(formData)
            .then(result => {
                if (result.success) {
                    form.reset();
                    document.getElementById('booking-form-container').style.display = 'none';
                    // Refresh calendar
                    if (window.appraisalTheme && window.appraisalTheme.calendarManager) {
                        setTimeout(() => {
                            window.appraisalTheme.calendarManager.refreshCalendar('member');
                        }, 500);
                    }
                }
            })
            .catch(error => {
                console.error('FormManager: Booking error:', error);
            })
            .finally(() => {
                // Reset button state
                submitBtn.disabled = false;
                submitBtn.textContent = originalText;
            });
        } else {
            // Fallback to regular form submission
            this.handleAjaxForm(form);
        }
    }

    // Initialize appraiser selector (no longer needed - removed appraiser selector)
    initializeAppraiserSelector() {
        // Appraiser selection has been removed - calendar now shows all appraisers' availability
        // This method is kept for compatibility but does nothing
    }

    // Show notification
    showNotification(message, type = 'info') {
        if (window.appraisalTheme && window.appraisalTheme.showNotification) {
            window.appraisalTheme.showNotification(message, type);
        } else if (window.appraisalTheme && window.appraisalTheme.notificationManager) {
            // Direct access to notification manager
            window.appraisalTheme.notificationManager.show(message, type);
        } else {
            // Fallback to alert if notification system not available
            alert(message);
        }
    }

    // Initialize all forms
    initialize() {
        this.initializeValidation();
        this.initializeAjaxForms();
        this.initializeBookingForm();
        this.initializeAppraiserSelector();
        
        // Also try to initialize booking form after a delay in case DOM isn't ready
        setTimeout(() => {
            const form = document.getElementById('booking-form');
            if (form && !form.hasAttribute('data-form-initialized')) {
                this.setupBookingFormEventListeners(form);
            }
        }, 1000);
    }
}

// Export singleton instance
export const formManager = new FormManager();

